import { Button, Spinner } from "@chakra-ui/react";
import { colors } from "../../theme";

/**
 * AppButton - Reusable button component with variants
 * 
 * Variants:
 * - primary: Solid orange background, white text
 * - secondary: Outline with orange border, orange text
 * - ghost: No background, used in sidebar
 * - danger: Red background for delete actions
 * 
 * @param {string} variant - Button variant (primary, secondary, ghost, danger)
 * @param {boolean} isLoading - Show loading spinner
 * @param {boolean} disabled - Disable button
 * @param {string} size - Button size (sm, md, lg)
 * @param {ReactNode} leftIcon - Icon on left
 * @param {ReactNode} rightIcon - Icon on right
 * @param {ReactNode} children - Button content
 */
const AppButton = ({
  variant = "primary",
  isLoading = false,
  disabled = false,
  size = "md",
  leftIcon,
  rightIcon,
  children,
  ...rest
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case "primary":
        return {
          bg: colors.primary,
          color: "white",
          _hover: {
            bg: "#E64A08",
            _disabled: { bg: colors.primary },
          },
          _active: {
            bg: "#CC4207",
          },
        };
      case "secondary":
        return {
          bg: "transparent",
          color: colors.primary,
          border: "1px solid",
          borderColor: colors.primary,
          _hover: {
            bg: colors.background,
            _disabled: { bg: "transparent" },
          },
          _active: {
            bg: colors.surface,
          },
        };
      case "ghost":
        return {
          bg: "transparent",
          color: colors.textSecondary,
          _hover: {
            bg: "rgba(255, 83, 9, 0.1)",
            color: colors.primary,
            _disabled: { bg: "transparent" },
          },
          _active: {
            bg: "rgba(255, 83, 9, 0.2)",
          },
        };
      case "ghostActive":
        return {
          bg: "rgba(255, 83, 9, 0.15)",
          color: colors.primary,
          _hover: {
            bg: "rgba(255, 83, 9, 0.2)",
          },
        };
      case "danger":
        return {
          bg: "#EF4444",
          color: "white",
          _hover: {
            bg: "#DC2626",
            _disabled: { bg: "#EF4444" },
          },
          _active: {
            bg: "#B91C1C",
          },
        };
      case "dangerOutline":
        return {
          bg: "transparent",
          color: "#EF4444",
          border: "1px solid",
          borderColor: "#EF4444",
          _hover: {
            bg: "rgba(239, 68, 68, 0.1)",
            _disabled: { bg: "transparent" },
          },
        };
      default:
        return {
          bg: colors.primary,
          color: "white",
        };
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case "sm":
        return {
          px: 3,
          py: 1.5,
          fontSize: "sm",
          height: "32px",
        };
      case "lg":
        return {
          px: 6,
          py: 3,
          fontSize: "md",
          height: "48px",
        };
      case "md":
      default:
        return {
          px: 4,
          py: 2,
          fontSize: "sm",
          height: "40px",
        };
    }
  };

  return (
    <Button
      {...getVariantStyles()}
      {...getSizeStyles()}
      borderRadius="md"
      fontWeight="500"
      disabled={disabled || isLoading}
      _disabled={{
        opacity: 0.6,
        cursor: "not-allowed",
      }}
      transition="all 0.2s"
      {...rest}
    >
      {isLoading ? (
        <Spinner size="sm" mr={children ? 2 : 0} />
      ) : (
        leftIcon && <span style={{ marginRight: children ? "8px" : "0" }}>{leftIcon}</span>
      )}
      {children}
      {rightIcon && !isLoading && (
        <span style={{ marginLeft: children ? "8px" : "0" }}>{rightIcon}</span>
      )}
    </Button>
  );
};

export default AppButton;
